import {isObject} from '@config/utils';
import {
    getTileMatrixSet,
    limitMatrix,
    getMatrixIds,
    getDefaultMatrixId,
    parseTileMatrixSetOption
} from '../WMTSUtils';
import {getLayerUrl, optionsToVendorParams} from '../LayersUtils';
import {transform} from 'ol/proj';
import {toSize} from 'ol/size';

export default {
    /**
     * Creates the request object and it's metadata for WMTS GetFeatureInfo.
     * @param {object} layer
     * @param {object} options
     * @param {string} infoFormat
     * @param {string} viewer
     * @return {object} an object with `request`, containing request paarams, `metadata` with some info about the layer and the request, and `url` to send the request to.
     */
    buildRequest: (layer, props = {}, infoFormat, viewer, featureInfo) => {
        let {reverseIntersectionPolicy = false, coordinate, resolution, projection, maxItems = 10, tolerance = 0, params: defaultParams } = props;

        const lon = coordinate[0];
        const lat = coordinate[1];

        const source = layer.getSource();

        const pixelRatio = source.tilePixelRatio_;
        if (isNaN(pixelRatio)) {
            return {};
        }

        const projectionObj = source.getProjection(projection);
        const sourceProjectionObj = source.getProjection();

        const tileGrid = source.getTileGrid();
        if (!tileGrid) {
            tileGrid = source.getTileGridForProjection(projectionObj);
        }

        if (sourceProjectionObj && sourceProjectionObj !== projectionObj) {
            coordinate = transform(coordinate, projectionObj, sourceProjectionObj);
        }

        const z = tileGrid.getZForResolution(resolution);

        if (tileGrid.getResolutions().length <= z) {
            return {};
        }

        const tileMatrix = tileGrid.getMatrixIds()[z];
        const tileMatrixSet = source.getMatrixSet();


        const tileResolution = tileGrid.getResolution(z);
        const scale = resolution / tileResolution;

        let tileSize = tileGrid.getTileSize(z) || 256;
        tileSize = toSize(tileSize, source.tmpSize_);

        const tileOrigin = tileGrid.getOrigin(z);
        const adjustX = reverseIntersectionPolicy ? 0.5 : 0;
        const adjustY = reverseIntersectionPolicy ? 0.5 : 0;
        const xFromOrigin = Math.floor((lon - tileOrigin[0]) / resolution + adjustX);
        const yFromOrigin = Math.floor((tileOrigin[1] - lat) / resolution + adjustY);
        let tileCoordX = (scale * xFromOrigin) / tileSize[0];
        let tileCoordY = (scale * yFromOrigin) / tileSize[1];
        let tileCol;
        let tileRow;
        if (reverseIntersectionPolicy) {
            tileCol = Math.ceil(tileCoordX) - 1;
            tileRow = Math.ceil(tileCoordY) - 1;
        }
        else {
            tileCol = Math.floor(tileCoordX);
            tileRow = Math.floor(tileCoordY);
        }

        const tileI = Math.floor((tileCoordX - tileCol) * tileSize[0]);
        const tileJ = Math.floor((tileCoordY - tileRow) * tileSize[1]);

        const params = optionsToVendorParams({
            layerFilter: layer.layerFilter,
            filterObj: layer.filterObj,
            params: Object.assign({}, layer.baseParams, layer.params, defaultParams)
        });
        let title = layer.title || layer.get('title') || layer.name || layer.get('name');

        return {
            request: {
                service: 'WMTS',
                request: 'GetFeatureInfo',
                layer: source.getLayer(),
                version: source.getVersion(),
                infoformat: infoFormat,
                format: source.getFormat(),
                style: source.getStyle() || '',
                ...Object.assign({}, params),
                tilecol: tileCol,
                tilerow: tileRow,
                tilematrix: tileMatrix,
                tilematrixset: tileMatrixSet,
                i: tileI,
                j: tileJ,
                buffer: tolerance,
                feature_count: maxItems,
                exceptions: source.v13_ ? 'XML' : 'application/vnd.ogc.se_xml',
            },
            metadata: {
                title: isObject(title) ? title.default : title,
                regex: layer.featureInfoRegex,
                viewer,
                featureInfo,
            },
            url: getLayerUrl(layer).replace(/[?].*$/g, '')
        };
    },

    getIdentifyFlow__: (layer, basePath, params) =>
        Observable.defer(() => axios.get(basePath, { params }))
            .catch((e) => {
                if (e.data.indexOf("ExceptionReport") > 0) {
                    return Rx.Observable.bindNodeCallback( (data, callback) => parseString(data, {
                        tagNameProcessors: [stripPrefix],
                        explicitArray: false,
                        mergeAttrs: true
                    }, callback))(e.data).map(data => {
                        const code = get(data, "ExceptionReport.Exception.exceptionCode");
                        if (code === 'TileOutOfRange') {
                            return params.infoformat === 'text/plain' ? {data: 'no features were found'} : { data: { features: []}};
                        }
                        return e;
                    });

                }
                return e;
            })
};